import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Play, Loader, Send, Edit2, Calendar, Clock, Mail } from 'lucide-react';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

export default function Generate() {
  const [generating, setGenerating] = useState(false);
  const [newsletters, setNewsletters] = useState([]);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [emails, setEmails] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedHtml, setEditedHtml] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Načíst všechny emaily
      const emailsData = await api.getEmails({});
      setEmails(emailsData.emails || []);

      // Pre-select neanalyzované emaily
      const unanalyzedIds = (emailsData.emails || [])
        .filter(email => !email.analyzed)
        .map(email => email.id);
      setSelectedEmails(unanalyzedIds);

      // Načíst existující newslettery
      const newslettersData = await api.getNewsletters({});
      setNewsletters(newslettersData.newsletters || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailToggle = (emailId) => {
    setSelectedEmails(prev =>
      prev.includes(emailId)
        ? prev.filter(id => id !== emailId)
        : [...prev, emailId]
    );
  };

  const handleGenerate = async () => {
    if (selectedEmails.length === 0) {
      alert('Vyberte alespoň jeden email');
      return;
    }

    setGenerating(true);

    try {
      const result = await api.generateNewsletter({
        emailIds: selectedEmails,
        onlyUnanalyzed: false // používáme vlastní výběr
      });

      // Reload dat po generování
      await loadData();

      // Vybrat nově vytvořený newsletter
      setSelectedNewsletter(result.newsletter);
      alert('Newsletter byl úspěšně vygenerován!');
    } catch (error) {
      alert('Chyba při generování: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectNewsletter = (newsletter) => {
    setSelectedNewsletter(newsletter);
    setIsEditing(false);
    setEditedTitle(newsletter.title);
    setEditedHtml(newsletter.html);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedTitle(selectedNewsletter.title);
    setEditedHtml(selectedNewsletter.html);
  };

  const handleSaveEdit = async () => {
    try {
      await api.updateNewsletter(selectedNewsletter.id, {
        title: editedTitle,
        html: editedHtml
      });

      // Aktualizovat lokální stav
      setSelectedNewsletter({
        ...selectedNewsletter,
        title: editedTitle,
        html: editedHtml
      });

      setNewsletters(newsletters.map(n =>
        n.id === selectedNewsletter.id
          ? { ...n, title: editedTitle, html: editedHtml }
          : n
      ));

      setIsEditing(false);
      alert('Newsletter byl úspěšně upraven!');
    } catch (error) {
      alert('Chyba při ukládání: ' + error.message);
    }
  };

  const handlePublish = async () => {
    if (!selectedNewsletter) return;

    if (!confirm('Opravdu chcete publikovat newsletter na Beehiiv?')) return;

    try {
      await api.publishToBeehiiv(selectedNewsletter.id);
      alert('Newsletter byl úspěšně publikován!');

      // Reload pro aktualizaci statusu
      await loadData();
    } catch (error) {
      alert('Chyba při publikování: ' + error.message);
    }
  };

  const sortedEmails = [...emails].sort((a, b) => {
    // Neanalyzované první
    if (a.analyzed !== b.analyzed) {
      return a.analyzed ? 1 : -1;
    }
    // Pak podle data
    return new Date(b.receivedAt) - new Date(a.receivedAt);
  });

  const sortedNewsletters = [...newsletters].sort((a, b) =>
    new Date(b.createdAt) - new Date(a.createdAt)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Generovat Newsletter</h1>

      <div className="grid grid-cols-12 gap-6">
        {/* Levý sloupec - Archiv návrhů */}
        <div className="col-span-3">
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Archiv návrhů</h2>

            <div className="space-y-2">
              {sortedNewsletters.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Zatím žádné návrhy
                </p>
              ) : (
                sortedNewsletters.map(newsletter => (
                  <button
                    key={newsletter.id}
                    onClick={() => handleSelectNewsletter(newsletter)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedNewsletter?.id === newsletter.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-sm mb-1 line-clamp-2">
                      {newsletter.title}
                    </div>
                    <div className="flex items-center text-xs text-gray-500 space-x-2">
                      <Calendar size={12} />
                      <span>
                        {format(new Date(newsletter.createdAt), 'd. M. yyyy', { locale: cs })}
                      </span>
                    </div>
                    {newsletter.published && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                        Publikováno
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Střední sloupec - Náhled/Editace */}
        <div className="col-span-6">
          {selectedNewsletter ? (
            <div className="card">
              {isEditing ? (
                // Editační režim
                <div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Název</label>
                    <input
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      className="input w-full"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">HTML obsah</label>
                    <textarea
                      value={editedHtml}
                      onChange={(e) => setEditedHtml(e.target.value)}
                      rows={20}
                      className="input w-full font-mono text-sm"
                    />
                  </div>

                  <div className="flex space-x-2">
                    <button onClick={handleSaveEdit} className="btn btn-primary">
                      Uložit změny
                    </button>
                    <button onClick={() => setIsEditing(false)} className="btn btn-secondary">
                      Zrušit
                    </button>
                  </div>
                </div>
              ) : (
                // Náhledový režim
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold">{selectedNewsletter.title}</h2>
                    <div className="flex space-x-2">
                      <button onClick={handleEdit} className="btn btn-secondary flex items-center space-x-2">
                        <Edit2 size={16} />
                        <span>Upravit</span>
                      </button>
                      <button onClick={handlePublish} className="btn btn-primary flex items-center space-x-2">
                        <Send size={16} />
                        <span>Publikovat</span>
                      </button>
                    </div>
                  </div>

                  <div className="prose max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: selectedNewsletter.html }} />
                  </div>

                  <div className="mt-6 pt-6 border-t">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Počet emailů:</span>
                        <span className="ml-2 font-semibold">
                          {selectedNewsletter.metadata?.emailCount || 0}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Vytvořeno:</span>
                        <span className="ml-2 font-semibold">
                          {format(new Date(selectedNewsletter.createdAt), 'd. M. yyyy HH:mm', { locale: cs })}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Status:</span>
                        <span className="ml-2">
                          <span className={`badge ${selectedNewsletter.published ? 'badge-success' : 'badge-warning'}`}>
                            {selectedNewsletter.published ? 'Publikováno' : 'Návrh'}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="card text-center py-12">
              <Mail size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">
                {generating
                  ? 'Generování newsletteru pomocí Claude AI...'
                  : 'Vyberte emaily vpravo a klikněte na Generovat'}
              </p>
            </div>
          )}
        </div>

        {/* Pravý sloupec - Výběr emailů */}
        <div className="col-span-3">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Emaily</h2>
              <span className="text-sm text-gray-600">
                {selectedEmails.length} vybráno
              </span>
            </div>

            <button
              onClick={handleGenerate}
              disabled={generating || selectedEmails.length === 0}
              className="btn btn-primary w-full mb-4 flex items-center justify-center space-x-2"
            >
              {generating ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  <span>Generuji...</span>
                </>
              ) : (
                <>
                  <Play size={20} />
                  <span>Generovat</span>
                </>
              )}
            </button>

            <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
              {sortedEmails.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Žádné emaily
                </p>
              ) : (
                sortedEmails.map(email => (
                  <label
                    key={email.id}
                    className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedEmails.includes(email.id)
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEmails.includes(email.id)}
                      onChange={() => handleEmailToggle(email.id)}
                      className="mt-1 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span
                          className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: email.metadata?.color || '#9E9E9E' }}
                        />
                        <span className={`text-xs font-medium ${email.analyzed ? 'text-gray-400' : 'text-gray-600'}`}>
                          {email.sourceName}
                        </span>
                      </div>
                      <div className="text-sm font-medium text-gray-900 line-clamp-2 mb-2">
                        {email.subject}
                      </div>
                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <Calendar size={12} />
                        <span>
                          {format(new Date(email.receivedAt), 'd. M. yyyy', { locale: cs })}
                        </span>
                        <Clock size={12} className="ml-2" />
                        <span>
                          {format(new Date(email.receivedAt), 'HH:mm', { locale: cs })}
                        </span>
                      </div>
                      {email.analyzed && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                          Analyzováno
                        </span>
                      )}
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
